import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import socket from "../../../services/socket-io";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx-js-style";
import { useTitle } from "../../../hooks/useTitle";
import { makeStyles } from "@material-ui/core/styles";

import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	IconButton,
	Grid,
	Chip,
	CircularProgress,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	InputAdornment,
	Box,
	Tooltip,
	ButtonGroup
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import {
	Description as ExcelIcon,
	Edit as EditIcon,
	Clear as ClearIcon,
	Search as SearchIcon,
	DateRange as DateRangeIcon,
	Sort as SortIcon,
	EventNote as EventNoteIcon
} from "@material-ui/icons";

import TableRowSkeleton from "../../../components/TableRowSkeleton";
import toastError from "../../../errors/toastError";
import api from "../../../services/api";
import PaymentModal from "../../../components/PaymentModal";
import { AuthContext } from "../../../context/Auth/AuthContext";
import AppointmentActionsModal from "../../../components/AppointmentActionsModal";

const useStyles = makeStyles((theme) => ({
	filterContainer: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(3),
		backgroundColor: theme.palette.background.default,
		borderRadius: theme.shape.borderRadius,
		border: "1px solid " + theme.palette.divider,
	},
	dateGroup: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
			alignItems: "stretch"
		}
	},
	actionButton: {
		height: "100%",
		minHeight: 40,
		backgroundColor: "#2e7d32",
		color: "white",
		"&:hover": {
			backgroundColor: "#1b5e20",
		}
	}
}));

registerLocale("es", es);

const ITEMS_PER_PAGE = 20;

const PendingPayments = () => {
	useTitle("Pendientes de Cobro");
	const classes = useStyles();
	const { user: currentUser } = useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [appointments, setAppointments] = useState([]);
	const [doctors, setDoctors] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [count, setCount] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedAppointment, setSelectedAppointment] = useState(null);
	const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedDoctorId, setSelectedDoctorId] = useState("");
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(new Date());
	const [orderBy, setOrderBy] = useState("date");
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [activeRange, setActiveRange] = useState("today");

	const toLocalDateParam = (date) => {
		if (!date) return null;
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, "0");
		const d = String(date.getDate()).padStart(2, "0");
		return `${y}-${m}-${d}`;
	};
	
	useEffect(() => {
		const fetchDoctors = async () => {
			try {
				const { data } = await api.get("/users", { params: { profile: "doctor" } });
				setDoctors(data.users || []);
			} catch (err) {
				toastError(err);
			}
		};
		if (currentUser.profile !== "doctor") {
			fetchDoctors();
		}
	}, [currentUser]);

	useEffect(() => {
		const fetchPayable = async () => {
			setLoading(true);
			try {
				const params = {
					pageNumber,
					orderBy,
					startDate: toLocalDateParam(startDate),
					endDate: toLocalDateParam(endDate),
				};

				if (searchParam) {
					if (!isNaN(searchParam)) {
						params.paymentId = searchParam;
					} else {
						params.searchParam = searchParam;
					}
				}

				if (currentUser.profile === "doctor") {
					params.doctorId = currentUser.id;
				} else if (selectedDoctorId) {
					params.doctorId = selectedDoctorId;
				}

				const { data } = await api.get("/appointments/payable", { params });
				setAppointments(data.appointments || []);
				setCount(data.count || 0);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};

		const delayDebounce = setTimeout(() => {
			fetchPayable();
		}, 500);

		return () => clearTimeout(delayDebounce);
	}, [searchParam, selectedDoctorId, startDate, endDate, orderBy, pageNumber, currentUser, refreshTrigger]);

	useEffect(() => {
		socket.on("appointment", (data) => {
			if (data.action === "update" || data.action === "create") {
				const app = data.appointment;
			}
		});
		return () => socket.off("appointment");
	}, []);

	const handleSetDateRange = (range) => {
		setActiveRange(range);
		const today = new Date();

		if (range === "today") {
			setStartDate(today);
			setEndDate(today);
		} else if (range === "yesterday") {
			const yesterday = new Date(today);
			yesterday.setDate(today.getDate() - 1);
			setStartDate(yesterday);
			setEndDate(yesterday);
		} else if (range === "this_month") {
			const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
			const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
			setStartDate(firstDay);
			setEndDate(lastDay);
		} else if (range === "all_time") {
			setStartDate(null);
			setEndDate(null);
		}
	};

	const handleExportExcel = async () => {
		setExporting(true);
		try {
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			
			const params = {
				startDate: toLocalDateParam(startDate),
				endDate: toLocalDateParam(endDate),
				orderBy: orderBy,
				timeZone: timeZone
			};

			if (searchParam) params.searchParam = searchParam;
			if (currentUser.profile === "doctor") params.doctorId = currentUser.id;
			else if (selectedDoctorId) params.doctorId = selectedDoctorId;

			const { data } = await api.get("/appointments/payable", { params });
			const history = data.appointments || [];

			const styles = {
				title: {
					font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
					fill: { fgColor: { rgb: "1976D2" } },
					alignment: { horizontal: "center", vertical: "center" }
				},
				header: {
					font: { bold: true, color: { rgb: "FFFFFF" } },
					fill: { fgColor: { rgb: "455A64" } },
					alignment: { horizontal: "center" },
					border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }
				},
				cell: {
					alignment: { vertical: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				cellCenter: {
					alignment: { vertical: "center", horizontal: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				currency: {
					numFmt: "$ #,##0.00",
					alignment: { vertical: "center", horizontal: "right" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				statusPaid: {
					fill: { fgColor: { rgb: "C8E6C9" } },
					font: { color: { rgb: "2E7D32" }, bold: true },
					alignment: { vertical: "center", horizontal: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				statusPartial: {
					fill: { fgColor: { rgb: "BBDEFB" } },
					font: { color: { rgb: "1565C0" }, bold: true },
					alignment: { vertical: "center", horizontal: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				statusCourtesy: {
					fill: { fgColor: { rgb: "FFE0B2" } },
					font: { color: { rgb: "E65100" }, bold: true },
					alignment: { vertical: "center", horizontal: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				statusPending: {
					fill: { fgColor: { rgb: "FFCDD2" } },
					font: { color: { rgb: "C62828" }, bold: true },
					alignment: { vertical: "center", horizontal: "center" },
					border: { bottom: { style: "thin", color: { rgb: "E0E0E0" } } }
				},
				totalRow: {
					font: { bold: true },
					fill: { fgColor: { rgb: "F5F5F5" } },
					border: { top: { style: "double" } }
				},
				metaData: {
					font: { italic: true, color: { rgb: "555555" } },
					alignment: { horizontal: "left" }
				}
			};

			const titleRow = [{ v: "LISTA DE CITAS PENDIENTES DE PAGO", s: styles.title }];

			const longDateOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric" };
			const fullDateTimeOptions = { 
				day: "numeric", month: "long", year: "numeric", 
				hour: "2-digit", minute: "2-digit", hour12: false 
			};
			
			const timeOpts = { hour: "2-digit", minute: "2-digit", hour12: false };

			const now = new Date();

			const generatedText = "Generado el: " + 
				now.toLocaleDateString("es-ES", longDateOptions) + 
				" a las " + 
				now.toLocaleTimeString("es-ES", timeOpts) + " hs";

			const startText = startDate.toLocaleDateString("es-ES", longDateOptions);
			const endText = endDate.toLocaleDateString("es-ES", longDateOptions);
			const periodText = "Período: Del " + startText + " al " + endText;

			const generatedRow = [{ v: generatedText, s: styles.metaData }];
			const periodRow = [{ v: periodText, s: styles.metaData }];

			const headers = ["Paciente", "Doctor", "Fecha Cita", "Fecha Último Pago", "Falta Cobrar ($)", "Estado", "Registrado Por"];
			const headerRow = headers.map(h => ({ v: h, s: styles.header }));

			let totalCollected = 0;

			console.log(history[0]);
			const dataRows = history.map(app => {
				const restaPagar = parseFloat(app.totalAmount - app.totalPaid);
				totalCollected += restaPagar;

				let assistants = new Set();
				if (app.payments && app.payments.length > 0) {
					app.payments.forEach(p => { if (p.assistant?.name) assistants.add(p.assistant.name); });
				}
				if (assistants.size === 0 && app.paymentStatus === "courtesy" && app.priceAdjustments?.length > 0) {
					app.priceAdjustments.forEach(adj => { if (adj.assistant?.name) assistants.add(adj.assistant.name); });
				}
				const registeredBy = assistants.size > 0 ? Array.from(assistants).join(", ") : "—";

				let dateStr = "—";
				if (app.date && app.startTime) {
					const [yyyy, mm, dd] = app.date.split("-");
					const time = app.startTime.slice(0, 5);
					dateStr = dd + "/" + mm + "/" + yyyy + " " + time;
				}
				
				const payDateStr = app.lastPaymentDate 
					? new Date(app.lastPaymentDate).toLocaleString(undefined, { 
							year: "numeric", 
							month: "2-digit", 
							day: "2-digit", 
							hour: "2-digit", 
							minute: "2-digit", 
							hour12: false 
						}) 
					: "—";

				let statusLabel = "Pendiente";
				let statusStyle = styles.statusPending;

				if (app.paymentStatus === "paid") {
					statusLabel = "Pagado Total";
					statusStyle = styles.statusPaid;
				} else if (app.paymentStatus === "courtesy") {
					statusLabel = "Cortesía";
					statusStyle = styles.statusCourtesy;
				} else if (app.paymentStatus === "partially_paid") {
					statusLabel = "Parcial";
					statusStyle = styles.statusPartial;
				}

				return [
					{ v: app.patientName || (app.contact && app.contact.name) || "—", s: styles.cell },
					{ v: (app.doctor && app.doctor.name) || "—", s: styles.cell },
					{ v: dateStr, s: styles.cellCenter },
					{ v: payDateStr, s: styles.cellCenter },
					{ v: restaPagar, s: styles.currency },
					{ v: statusLabel, s: statusStyle },
					{ v: registeredBy, s: styles.cell }
				];
			});

			const totalRow = [
				{ v: "TOTAL PENDIENTE DE COBRO", s: styles.totalRow },
				{ v: "", s: styles.totalRow },
				{ v: "", s: styles.totalRow },
				{ v: "", s: styles.totalRow },
				{ v: totalCollected, s: { ...styles.totalRow, ...styles.currency } },
				{ v: "", s: styles.totalRow },
				{ v: "", s: styles.totalRow }
			];

			const finalData = [
				titleRow,
				[],
				generatedRow,
				periodRow,
				[],
				headerRow,
				...dataRows,
				totalRow
			];

			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.aoa_to_sheet(finalData);

			ws["!cols"] = [
				{ wch: 30 },
				{ wch: 25 },
				{ wch: 20 },
				{ wch: 20 },
				{ wch: 15 },
				{ wch: 15 },
				{ wch: 25 }
			];

			ws["!merges"] = [
				{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
				{ s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
				{ s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
				{ s: { r: 6 + dataRows.length, c: 0 }, e: { r: 6 + dataRows.length, c: 3 } }
			];

			XLSX.utils.book_append_sheet(wb, ws, "Historial");

			const fileName = "Historial_Cobros_Pendientes_" + new Date().toISOString().slice(0,10) + ".xlsx";
			XLSX.writeFile(wb, fileName);

			toast.success("Exportado correctamente");
		} catch (err) {
			toastError(err);
		} finally {
			setExporting(false);
		}
	};
	 
	const handleOpenModal = (app) => {
		setSelectedAppointment(app);
		setModalOpen(true);
	};

	const handleCloseModal = (shouldRefresh) => {
		setModalOpen(false);
		setSelectedAppointment(null);
		if (shouldRefresh === true) {
			setRefreshTrigger((prev) => prev + 1);
		}
	};

	const getStatusColor = (status) => {
		if (status === "completed") return "#4caf50";
		if (status === "scheduled") return "#2196f3";
		if (status === "cancelled") return "#f44336";
		return "#9e9e9e";
	};

	const getStatusLabel = (status) => {
		if (status === "completed") return "Completada";
		if (status === "scheduled") return "Programada";
		if (status === "cancelled") return "Cancelada";
		return status;
	};

	const handleOpenAppointmentModal = (app) => {
		setSelectedAppointment(app);
		setAppointmentModalOpen(true);
	};

	const handleCloseAppointmentModal = (shouldRefresh) => {
		setAppointmentModalOpen(false);
		if (shouldRefresh) {
			setRefreshTrigger((prev) => prev + 1);
		}
		setSelectedAppointment(null);
	};

	const handleCancelAppointment = async (id) => {
		try {
			await api.patch("/appointments/" + id, { status: "cancelled" });
			toast.success("Cita cancelada correctamente");
			handleCloseAppointmentModal(true);
		} catch (err) {
			toastError(err);
		}
	};

	const handleCompleteAppointment = async (id) => {
		try {
			await api.patch("/appointments/" + id, { status: "completed" });
			toast.success("Cita finalizada correctamente");
			handleCloseAppointmentModal(true);
		} catch (err) {
			toastError(err);
		}
	};
	
	return (
		<Paper variant="outlined" style={{ padding: 16 }}>
			{selectedAppointment && (
				<PaymentModal
					open={modalOpen}
					onClose={handleCloseModal}
					appointment={selectedAppointment}
					mode="edit"
				/>
			)}
			{selectedAppointment && (
				<AppointmentActionsModal
					open={appointmentModalOpen}
					appointment={selectedAppointment}
					onClose={handleCloseAppointmentModal}
					onCancelAppointment={handleCancelAppointment}
					onCompleteAppointment={handleCompleteAppointment}
					disableReprogram={true}
					mode={selectedAppointment.status === "scheduled" ? "edit" : "view"}
				/>
			)}

			<div className={classes.filterContainer}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							label="Paciente o ID de Pago"
							variant="outlined"
							size="small"
							fullWidth
							value={searchParam}
							onChange={(e) => setSearchParam(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon color="action" />
									</InputAdornment>
								),
								endAdornment: searchParam && (
									<InputAdornment position="end">
										<IconButton size="small" onClick={() => setSearchParam("")}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								)
							}}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						{currentUser.profile !== "doctor" ? (
							<FormControl variant="outlined" size="small" fullWidth>
								<InputLabel>Doctor</InputLabel>
								<Select
									value={selectedDoctorId}
									onChange={(e) => setSelectedDoctorId(e.target.value)}
									label="Doctor"
									MenuProps={{
										anchorOrigin: { vertical: "bottom", horizontal: "left" },
										transformOrigin: { vertical: "top", horizontal: "left" },
										getContentAnchorEl: null
									}}
								>
									<MenuItem value="">Todos</MenuItem>
									{doctors.map(d => (
										<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
									))}
								</Select>
							</FormControl>
						) : (
							<TextField
								disabled
								variant="outlined"
								size="small"
								fullWidth
								value={"Doctor: " + currentUser.name}
							/>
						)}
					</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<FormControl variant="outlined" size="small" fullWidth>
							<InputLabel>Ordenar</InputLabel>
							<Select
								value={orderBy}
								onChange={(e) => setOrderBy(e.target.value)}
								label="Ordenar"
								startAdornment={<InputAdornment position="start"><SortIcon fontSize="small" color="action" /></InputAdornment>}
								MenuProps={{
									getContentAnchorEl: null,
									anchorOrigin: { vertical: "bottom", horizontal: "left" },
									transformOrigin: { vertical: "top", horizontal: "left" }
								}}
							>
								<MenuItem value="date">Fecha Cita</MenuItem>
								<MenuItem value="lastPayment">Último Pago</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid item xs={12}>
						<Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
							<div className={classes.dateGroup}>
								<ButtonGroup variant="outlined" size="small" style={{ marginRight: 8 }}>
									<Button
										color={activeRange === "today" ? "primary" : "default"}
										onClick={() => handleSetDateRange("today")}
									>
										Hoy
									</Button>
									<Button
										color={activeRange === "yesterday" ? "primary" : "default"}
										onClick={() => handleSetDateRange("yesterday")}
									>
										Ayer
									</Button>
									<Button
										color={activeRange === "this_month" ? "primary" : "default"}
										onClick={() => handleSetDateRange("this_month")}
									>
										Mes
									</Button>
									<Button
										color={activeRange === "all_time" ? "primary" : "default"}
										onClick={() => handleSetDateRange("all_time")}
									>
										Todo
									</Button>
								</ButtonGroup>

								<Box display="flex" gap={1}>
									<DatePicker
										selected={startDate}
										onChange={(date) => { setStartDate(date); setActiveRange(null); }}
										selectsStart
										startDate={startDate}
										endDate={endDate}
										locale="es"
										dateFormat="dd/MM/yyyy"
										customInput={
											<TextField
												label="Desde"
												variant="outlined"
												size="small"
												style={{ width: 150 }}
												InputProps={{
													startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" /></InputAdornment>
												}}
											/>
										}
									/>
									<DatePicker
										selected={endDate}
										onChange={(date) => { setEndDate(date); setActiveRange(null); }}
										selectsEnd
										startDate={startDate}
										endDate={endDate}
										minDate={startDate}
										locale="es"
										dateFormat="dd/MM/yyyy"
										customInput={
											<TextField
												label="Hasta"
												variant="outlined"
												size="small"
												style={{ width: 150 }}
												InputProps={{
													startAdornment: <InputAdornment position="start"><DateRangeIcon fontSize="small" /></InputAdornment>
												}}
											/>
										}
									/>
								</Box>
							</div>

							<Button
								variant="contained"
								className={classes.actionButton}
								startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <ExcelIcon />}
								onClick={handleExportExcel}
								disabled={exporting || loading}
							>
								Exportar Excel
							</Button>
						</Box>
					</Grid>
				</Grid>
			</div>

			<div style={{ overflowX: "auto" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Paciente</TableCell>
							{currentUser.profile !== "doctor" && (
								<TableCell align="center" style={{ fontWeight: "bold" }}>Doctor</TableCell>
							)}
							<TableCell align="center" style={{ fontWeight: "bold" }}>Fecha Cita</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Último Pago</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Estado</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Total</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Pagado</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Saldo</TableCell>
							<TableCell align="center" style={{ fontWeight: "bold" }}>Acción</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton columns={9} />
						) : appointments.length > 0 ? (
							appointments.map((app) => {
								const total = parseFloat(app.totalAmount || 0);
								const paid = parseFloat(app.totalPaid || 0);
								const remaining = total - paid;

								let dateStr = "—";
								if (app.date && app.startTime) {
									const [yyyy, mm, dd] = app.date.split("-");
									const time = app.startTime.slice(0, 5);
									dateStr = dd + "/" + mm + "/" + yyyy + " " + time;
								}

								const lastPayStr = app.lastPaymentDate || (app.dataValues && app.dataValues.lastPaymentDate)
									? new Date(app.lastPaymentDate || app.dataValues.lastPaymentDate).toLocaleString(undefined, {
										year: "numeric",
										month: "2-digit",
										day: "2-digit",
										hour: "2-digit",
										minute: "2-digit",
										hour12: false
									})
									: "—";
								return (
									<TableRow key={app.id} hover>
										<TableCell align="center">
											<div style={{ fontWeight: 500 }}>
												{app.patientName || (app.contact && app.contact.name) || "—"}
											</div>
										</TableCell>
										{currentUser.profile !== "doctor" && (
 											<TableCell align="center">
 												{(app.doctor && app.doctor.name) || "—"}
 											</TableCell>
 										)}
										<TableCell align="center">
											{dateStr}
										</TableCell>
										<TableCell align="center">
											{lastPayStr}
										</TableCell>
										<TableCell align="center">
											<Chip
												label={getStatusLabel(app.status)}
												size="small"
												style={{
													backgroundColor: getStatusColor(app.status),
													color: "#fff",
													fontWeight: 600
												}}
											/>
										</TableCell>
										<TableCell align="center">${total.toFixed(2)}</TableCell>
										<TableCell align="center">${paid.toFixed(2)}</TableCell>
										<TableCell align="center" style={{ color: "#d32f2f", fontWeight: "bold" }}>
											${remaining.toFixed(2)}
										</TableCell>
										<TableCell align="center" style={{ whiteSpace: "nowrap" }}>
											<Tooltip title="Ver/Editar Detalles de Cita">
												<IconButton
													size="small"
													onClick={() => handleOpenAppointmentModal(app)}
													style={{ marginRight: 4 }}
												>
													<EventNoteIcon color="action" />
												</IconButton>
											</Tooltip>

											<Tooltip title="Gestionar Pago">
												<IconButton
													size="small"
													onClick={() => handleOpenModal(app)}
													color="primary"
												>
													<EditIcon />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={9} align="center" style={{ padding: 24, color: "#888" }}>
									No se encontraron citas con pagos pendientes en los filtros actuales.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			<Box mt={2} display="flex" justifyContent="center">
				<Pagination
					count={Math.ceil(count / ITEMS_PER_PAGE)}
					page={pageNumber}
					onChange={(e, v) => setPageNumber(v)}
					color="primary"
					showFirstButton
					showLastButton
				/>
			</Box>
		</Paper>
	);
};

export default PendingPayments;